/** @jsx jsx */
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { jsx } from "@emotion/core";

import moment from "moment";

import {
  Table,
  Typography,
  Radio,
  Icon,
  Button,
  Pagination,
  Row,
  Select
} from "antd";
import { ColumnProps } from "antd/lib/table";

import {
  BillsListContents as CONSTANTS,
  BillListType
} from "./BillsListConstants";

import useAPI from "../../../helperHooks/useAPI";
import useDocumentDownload from "../../../helperHooks/useDocumentDownload";

import { BillService } from "../../../services/billService/index";
import {
  GetBillListResponse,
  BillDetails,
  BillStatus
} from "../../../services/billService/BillService.type";

import {
  billsListStyles,
  tableScroll,
  billFilterStyles,
  radioButtonStyles
} from "./BillsListStyles";
import {
  TABLE_ROW_CLASS_NAME,
  DATE_FORMAT,
  LIST_FETCH_SIZE,
  SORT_ORDER,
  TABLE_TOTAL_COUNT
} from "../../../Constants";
import { localNumber } from "../../../helper/Format";

const { Text } = Typography;
const { Option } = Select;

interface BillsListProps {
  // The following props appear when calling component from the Home page.
  inBillsCard?: boolean;
  className?: string;
  billsListResponse?: GetBillListResponse;
}

const BillsList: React.FC<BillsListProps> = ({
  inBillsCard,
  className,
  billsListResponse
}: BillsListProps) => {
  const { callAPI: getBillList, ...billListData } = useAPI(
    BillService.getBills
  );

  const { loading, response } = billListData;

  const { download } = useDocumentDownload();
  const [billListTableData, setBillListTableData] = useState<BillDetails[]>([]);
  const [statusFilter, setStatusFilter] = useState<BillStatus>();
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState(SORT_ORDER.Descend);
  const totalItemCount = useMemo(
    () =>
      (response?.noOfPaidWithReceipt || 0) +
      (response?.noOfPending || 0) +
      (response?.noOfPaidWithoutReceipt || 0) +
      (response?.noOfRejected || 0) +
      (response?.noOfApproved || 0),
    [response]
  );

  const handleFilterChange = (activeKey: BillStatus) => {
    setStatusFilter(activeKey);
    setPage(1);
  };
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleSortChange = (order: string) => {
    setSort(order as SORT_ORDER);
  };

  useEffect(() => {
    // Avoid setting state infinitely.
    //To support bills dashboard card
    if (inBillsCard) {
      setBillListTableData(billsListResponse?.challanDetailsSet || []);
    }
  }, [inBillsCard, billsListResponse]);

  const getQuery = useCallback(() => {
    const query = {
      page,
      page_size: LIST_FETCH_SIZE,
      sortBy: sort === SORT_ORDER.Ascend ? "updated_on" : "-updated_on"
    };
    if (statusFilter) {
      Object.assign(query, { paymentStatus: statusFilter });
    }
    return query;
  }, [statusFilter, page, sort]);

  //Get count of all types of challans
  const getBillCount = (billStatus: BillStatus | undefined) => {
    switch (billStatus) {
      case BillStatus.Approved:
        return response?.noOfApproved;
      case BillStatus.Rejected:
        return response?.noOfRejected;
      case BillStatus.Pending:
        return response?.noOfPending;
      case BillStatus["Receipt Uploaded"]:
        return response?.noOfPaidWithReceipt;
      case BillStatus["Pending Receipt"]:
        return response?.noOfPaidWithoutReceipt;
      default:
        return totalItemCount;
    }
  };

  const billListSuccessHandler = (billListResponse?: GetBillListResponse) => {
    if (!billListResponse) {
      return;
    } else {
      setBillListTableData(billListResponse.challanDetailsSet);
    }
  };

  useEffect(() => {
    if (!inBillsCard) {
      const query = getQuery();
      getBillList({ query }, billListSuccessHandler);
    }
  }, [getBillList, inBillsCard, getQuery]);

  const columns: Array<ColumnProps<BillDetails>> = [
    {
      title: CONSTANTS.labels.challanId,
      dataIndex: "challanId",
      key: "challanId",
      width: 150,
      ellipsis: true,
      render: (challanId: string, row: BillListType) => {
        return (
          <Button
            type="link"
            onClick={() => {
              download(row.jitfinS3Key);
            }}
            className="downloadable-link p-0"
          >
            {challanId}
          </Button>
        );
      }
    },
    {
      title: CONSTANTS.labels.type,
      dataIndex: "type",
      key: "type",
      width: 100,
      ellipsis: true
    },
    {
      title: CONSTANTS.labels.updatedOn,
      dataIndex: "updatedOn",
      key: "updatedOn",
      width: 150,
      ellipsis: true,
      render: (updatedOn: string) => {
        return moment(updatedOn).format(DATE_FORMAT);
      },
      sorter: !inBillsCard // Hide sort in Home page.
    },
    {
      title: CONSTANTS.labels.challanAmount,
      dataIndex: "challanAmount",
      key: "challanAmount",
      width: 150,
      ellipsis: true,
      render: (challanAmount: number) => localNumber(challanAmount || 0)
    },
    {
      title: CONSTANTS.labels.status,
      dataIndex: "paymentStatus",
      key: "paymentStatus",
      width: 150,
      ellipsis: true,
      render: (paymentStatus: string) => {
        let status = paymentStatus;
        if (
          paymentStatus === BillStatus["Pending Receipt"] ||
          paymentStatus === BillStatus["Receipt Uploaded"]
        ) {
          status = "paid";
        }
        return (
          <Text
            className={
              "d-flex justify-content-center align-items-center " + status
            }
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </Text>
        );
      }
    },
    {
      title: CONSTANTS.labels.remark,
      dataIndex: "receiptS3Key",
      key: "receiptS3Key",
      width: 150,
      ellipsis: true,
      render: (receiptS3Key: string, row: BillListType) => {
        if (row.paymentStatus === BillStatus.Rejected) {
          return row.rejectReason;
        } else if (row.paymentStatus === BillStatus["Receipt Uploaded"]) {
          return (
            <Button
              type="link"
              onClick={() => {
                download(receiptS3Key);
              }}
              className="downloadable-link"
            >
              {CONSTANTS.labels.receipt}
            </Button>
          );
        } else {
          return <Icon type="line" />;
        }
      }
    }
  ];
  return (
    <div
      css={billsListStyles}
      className={`${
        inBillsCard ? `bills-list ${className}` : "px-4 pt-4 pb-3"
      }`}
    >
      {!inBillsCard && (
        <Row type="flex" className="justify-content-between">
          <Radio.Group defaultValue={CONSTANTS.labels.gst} className="mb-3">
            <Radio.Button
              value={CONSTANTS.labels.gst}
              style={radioButtonStyles}
              className="px-2"
            >
              {CONSTANTS.labels.gst}
            </Radio.Button>
          </Radio.Group>
          <Select
            optionLabelProp="label"
            css={billFilterStyles}
            allowClear
            showSearch
            value={statusFilter}
            placeholder={CONSTANTS.placeHolders.filter}
            onChange={handleFilterChange}
            dropdownClassName="select-status"
            className="mb-2"
            filterOption={(
              input,
              option //to search the options when user is typing, ignoring case
            ) => {
              return (
                (option.key?.toString().toLowerCase() || "").indexOf(
                  input.toLowerCase()
                ) >= 0
              );
            }}
          >
            {Object.entries(BillStatus).map(([key, value]) => (
              <Option key={key} value={value} label={key}>
                <section key={key} className="d-flex justify-content-between">
                  {key}
                  <span>{getBillCount(value) || 0}</span>
                </section>
              </Option>
            ))}
          </Select>
        </Row>
      )}
      <Table
        className="bill-table"
        rowKey="id"
        dataSource={
          !!billListTableData.length && inBillsCard
            ? billListTableData.slice(-3) // Show the latest 3 updated bills in Home page.
            : billListTableData
        }
        columns={columns}
        rowClassName={TABLE_ROW_CLASS_NAME}
        pagination={false}
        loading={loading}
        scroll={tableScroll}
        onChange={(_, __, sorter) => handleSortChange(sorter.order)}
      />
      {!inBillsCard && (
        <Pagination
          hideOnSinglePage
          showQuickJumper
          showTotal={TABLE_TOTAL_COUNT}
          current={page}
          defaultPageSize={LIST_FETCH_SIZE}
          total={getBillCount(statusFilter)}
          onChange={handlePageChange}
        />
      )}
    </div>
  );
};
export default BillsList;
